import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaWhatsapp, FaLinkedin, FaRegCopy, FaCheck } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import PopupModal from "../../PopupModal";
import "./index.css";

const DIVIDER = "━━━━━━━━━━━━━━━━━━━━━━";

// Some stored job descriptions have HTML/section headings stripped without
// leaving whitespace behind (e.g. "...applications.Architecture: Architect…"
// or "About the RoleWe are looking…"). Re-insert a space so the text reads
// correctly when shared as plain text.
const cleanDescription = (text) =>
  text
    .replace(/\.(?=[A-Z])/g, ". ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

// Right-pad labels so the detail block lines up as a clean two-column list
// in a monospace chat/email client, without needing emoji as visual markers.
const FIELD_WIDTH = 17;
const field = (label, value) => `${`${label}:`.padEnd(FIELD_WIDTH)}${value}`;

const buildShareText = (job) => {
  const lines = [
    "EMPLOYEE REFERRAL OPPORTUNITY",
    DIVIDER,
    "",
    field("Position", job.title),
  ];

  if (job.department)     lines.push(field("Department", job.department));
  if (job.location)       lines.push(field("Location", job.location));
  if (job.experience)     lines.push(field("Experience", job.experience));
  if (job.employmentType) lines.push(field("Employment Type", job.employmentType));
  if (job.vacancies)      lines.push(field("Vacancies", job.vacancies));

  lines.push("", DIVIDER, "");

  if (job.description) lines.push(cleanDescription(job.description), "");

  if (job.skills?.length) {
    lines.push("Required Skills", ...job.skills.map((skill) => `• ${skill}`), "");
  }

  lines.push(
    DIVIDER,
    "",
    "If you know someone suitable,",
    "please refer them through the Employee Hiring Portal.",
    "",
    "#EmployeeReferral",
  );

  return lines.join("\n");
};

const ShareJobModal = ({ job, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimeoutRef.current), []);

  if (!job) return null;

  const jobUrl = window.location.href;
  const text = buildShareText(job);

  const openWindow = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const handleWhatsApp = () => {
    openWindow(`https://wa.me/?text=${encodeURIComponent(text)}`);
  };

  const handleLinkedIn = () => {
    openWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`);
  };

  const handleEmail = () => {
    openWindow(
      `mailto:?subject=${encodeURIComponent(`Job opening: ${job.title}`)}` +
      `&body=${encodeURIComponent(text)}`
    );
  };

  const handleCopyDetails = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Job details copied to clipboard");
      setCopied(true);
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy the job details. Please try again.");
    }
  };

  return (
    <PopupModal showModal={isOpen} modalName="Share Job" closeBtn={onClose} width="420px">
      <div className="share-job-modal">
        <p className="share-job-hint">Share this job with your network</p>

        <div className="share-job-options">
          <button type="button" className="share-job-option" onClick={handleWhatsApp}>
            <span className="share-job-icon share-job-icon--whatsapp"><FaWhatsapp /></span>
            WhatsApp
          </button>
          <button type="button" className="share-job-option" onClick={handleLinkedIn}>
            <span className="share-job-icon share-job-icon--linkedin"><FaLinkedin /></span>
            LinkedIn
          </button>
          <button type="button" className="share-job-option" onClick={handleEmail}>
            <span className="share-job-icon share-job-icon--email"><MdEmail /></span>
            Email
          </button>
          <button type="button" className="share-job-option" onClick={handleCopyDetails}>
            <span className={`share-job-icon share-job-icon--copy${copied ? " share-job-icon--copied" : ""}`}>
              {copied ? <FaCheck /> : <FaRegCopy />}
            </span>
            {copied ? "Copied!" : "Copy Details"}
          </button>
        </div>

        <div className="share-job-preview">
          <p className="share-job-preview-label">Job Preview</p>
          <p className="share-job-preview-title">{job.title}</p>
          <p className="share-job-preview-desc">{job.description}</p>
        </div>
      </div>
    </PopupModal>
  );
};

export default ShareJobModal;
